import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Space, Tag, Typography } from "antd";
import { getPerfDebugEventName, isPerfDebugEnabled } from "../lib/perfDebug";

const { Text } = Typography;
const MAX_EVENTS = 120;
const TRACKED_KEYS = [
    "system_selector.load",
    "system_selector.dropdown_populate",
    "app.load_tenants",
    "app.tenant_dropdown_populate",
    "process_metrics.load",
    "agent_metrics.load",
];

function formatDuration(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return `${num.toFixed(1)} ms`;
}

function summarizeEvents(events, name) {
    const bucket = events.filter((event) => event.name === name && Number.isFinite(Number(event.payload?.durationMs)));
    if (bucket.length === 0) {
        return { count: 0, avgMs: null, p95Ms: null, maxMs: null };
    }

    const values = bucket.map((event) => Number(event.payload.durationMs)).sort((left, right) => left - right);
    const sum = values.reduce((acc, value) => acc + value, 0);
    const p95Index = Math.min(values.length - 1, Math.max(0, Math.ceil(values.length * 0.95) - 1));

    return {
        count: values.length,
        avgMs: sum / values.length,
        p95Ms: values[p95Index],
        maxMs: values[values.length - 1],
    };
}

const PerfDebugOverlay = () => {
    const [enabled, setEnabled] = useState(() => isPerfDebugEnabled());
    const [events, setEvents] = useState([]);
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        const refreshEnabled = () => setEnabled(isPerfDebugEnabled());
        const interval = window.setInterval(refreshEnabled, 1000);
        return () => window.clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;

        const eventName = getPerfDebugEventName();
        const onPerfEvent = (event) => {
            const detail = event?.detail || {};
            const next = {
                name: String(detail.name || "unknown"),
                payload: detail.payload || {},
                ts: Number(detail.ts || Date.now()),
            };

            setEvents((prev) => {
                const updated = [...prev, next];
                if (updated.length <= MAX_EVENTS) return updated;
                return updated.slice(updated.length - MAX_EVENTS);
            });
        };

        window.addEventListener(eventName, onPerfEvent);
        return () => window.removeEventListener(eventName, onPerfEvent);
    }, [enabled]);

    const metrics = useMemo(() => TRACKED_KEYS.map((name) => ({ name, ...summarizeEvents(events, name) })), [events]);

    const latest = events.length > 0 ? events[events.length - 1] : null;

    if (!enabled) return null;

    return (
        <Card
            size="small"
            title="Perf Debug"
            style={{
                position: "fixed",
                right: 16,
                bottom: 16,
                width: collapsed ? 220 : 420,
                zIndex: 1200,
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
            extra={(
                <Space size={4}>
                    <Button size="small" onClick={() => setCollapsed((prev) => !prev)}>{collapsed ? "Expand" : "Collapse"}</Button>
                    <Button size="small" onClick={() => setEvents([])}>Clear</Button>
                </Space>
            )}
        >
            {collapsed ? (
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    <Text type="secondary">Events: {events.length}</Text>
                    <Text type="secondary">Latest: {latest ? latest.name : "-"}</Text>
                </Space>
            ) : (
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Text type="secondary">Enable with query perfDebug=1 or localStorage key datasnare:perf-debug=1.</Text>
                    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.6fr 0.8fr 0.8fr", gap: 6, alignItems: "center" }}>
                        <Text strong>Metric</Text>
                        <Text strong>Count</Text>
                        <Text strong>P95</Text>
                        <Text strong>Max</Text>
                        {metrics.map((row) => (
                            <React.Fragment key={row.name}>
                                <Text ellipsis title={row.name}>{row.name}</Text>
                                <Text>{row.count}</Text>
                                <Text>{formatDuration(row.p95Ms)}</Text>
                                <Text>{formatDuration(row.maxMs)}</Text>
                            </React.Fragment>
                        ))}
                    </div>
                    {latest ? (
                        <div>
                            <Tag color="blue">Latest</Tag>
                            <Text>{latest.name}</Text>
                            {Number.isFinite(Number(latest.payload?.durationMs)) ? (
                                <Tag style={{ marginLeft: 8 }}>{formatDuration(latest.payload.durationMs)}</Tag>
                            ) : null}
                        </div>
                    ) : (
                        <Text type="secondary">No events captured yet.</Text>
                    )}
                </Space>
            )}
        </Card>
    );
};

export default PerfDebugOverlay;
